/**
 * Watch Later Management API Service
 * This service handles communication with the Azure Functions backend for watch later functionality.
 * Provides 1:1 feature parity with favorites service.
 */

import axios from 'axios';

class WatchLaterService {
  constructor() {
    this.baseURL = process.env.REACT_APP_API_BASE || 'http://localhost:7071/api';
    // Ensure baseURL doesn't end with slash to avoid double slashes
    this.baseURL = this.baseURL.endsWith('/') ? this.baseURL.slice(0, -1) : this.baseURL;
    console.log('WatchLaterService initialized with baseURL:', this.baseURL);
  }

  /**
   * Get watch later items for a user
   * @param {string} userId - User ID
   * @param {Object} filters - Optional filters
   * @returns {Promise} - API response
   */
  async getWatchLaterItems(userId, filters = {}) {
    try {
      console.log('Getting watch later items for user:', userId, 'with filters:', filters);
      
      const params = { userId, ...filters };
      const response = await axios.get(`${this.baseURL}/watchLater/get`, { params });
      
      // Handle new Firebase response format
      if (response.data && response.data.data) {
        return response.data;
      }
      
      // Fallback for old format
      return { data: response.data, message: "Operation completed successfully" };
    } catch (error) {
      console.error('Error getting watch later items:', error);
      throw error;
    }
  }

  /**
   * Add an item to watch later
   * @param {string} userId - User ID (optional if included in watchLaterData)
   * @param {Object} watchLaterData - Watch later data
   * @returns {Promise} - API response
   */
  async addToWatchLater(userId, watchLaterData) {
    try {
      // Support both single parameter (watchLaterData with userId) and two parameters
      let actualUserId, actualData;
      
      if (typeof userId === 'object' && watchLaterData === undefined) {
        // Single parameter case: addToWatchLater(watchLaterData)
        actualData = userId;
        actualUserId = actualData.userId;
      } else {
        // Two parameter case: addToWatchLater(userId, watchLaterData)
        actualUserId = userId;
        actualData = watchLaterData;
      }
      
      console.log('Adding to watch later:', actualData);
      
      const response = await axios.post(`${this.baseURL}/watchLater/post`, {
        userId: actualUserId,
        itemId: actualData.itemId,
        itemType: actualData.itemType, // 'video', 'ebook', 'qna'
        title: actualData.title,
        description: actualData.description,
        category: actualData.category,
        addedAt: new Date().toISOString(),
        ...actualData
      });
      
      // Handle new Firebase response format
      if (response.data && response.data.data) {
        return response.data;
      }
      
      // Fallback for old format
      return { data: response.data, message: "Operation completed successfully" };
    } catch (error) {
      console.error('Error adding to watch later:', error);
      throw error;
    }
  }

  /**
   * Remove an item from watch later
   * @param {string} userId - User ID
   * @param {string} itemId - Item ID to remove
   * @param {string} itemType - Type of item (video, ebook, qna)
   * @returns {Promise} - API response
   */
  async removeFromWatchLater(userId, itemId, itemType) {
    try {
      console.log('Removing from watch later:', { userId, itemId, itemType });
      
      const response = await axios.delete(`${this.baseURL}/watchLater/delete`, {
        data: { userId, itemId, itemType }
      });
      
      // Handle new Firebase response format
      if (response.data && response.data.data) {
        return response.data;
      }
      
      // Fallback for old format
      return { data: response.data, message: "Operation completed successfully" };
    } catch (error) {
      console.error('Error removing from watch later:', error);
      throw error;
    }
  }

  /**
   * Update watch later metadata
   * @param {string} watchLaterId - Watch Later ID
   * @param {Object} updateData - Data to update
   * @returns {Promise} - API response
   */
  async updateWatchLater(watchLaterId, updateData) {
    try {
      console.log('Updating watch later:', watchLaterId, 'with data:', updateData);
      
      const response = await axios.put(`${this.baseURL}/watchLater/update`, {
        watchLaterId,
        ...updateData
      });
      
      // Handle new Firebase response format
      if (response.data && response.data.data) {
        return response.data;
      }
      
      // Fallback for old format
      return { data: response.data, message: "Operation completed successfully" };
    } catch (error) {
      console.error('Error updating watch later:', error);
      throw error;
    }
  }

  /**
   * Clear all watch later items for a user
   * @param {string} userId - User ID
   * @returns {Promise} - API response
   */
  async clearAllWatchLater(userId) {
    try {
      console.log('Clearing all watch later items for user:', userId);
      
      // Use correct backend endpoint with userId in query params
      const response = await axios.delete(`${this.baseURL}/watchLater/deleteAll?userId=${userId}`);
      
      // Handle new Firebase response format
      if (response.data && response.data.data) {
        return response.data;
      }
      
      // Fallback for old format
      return { data: response.data, message: "Operation completed successfully" };
    } catch (error) {
      console.error('Error clearing all watch later items:', error);
      throw error;
    }
  }

  /**
   * Check if item is in watch later
   * @param {string} userId - User ID
   * @param {string} itemId - Item ID
   * @param {string} itemType - Item type
   * @returns {Promise} - Boolean response
   */
  async isInWatchLater(userId, itemId, itemType) {
    try {
      const response = await this.getWatchLaterItems(userId, { type: itemType });
      const watchLaterData = response.data || [];
      return watchLaterData.some(item => item.itemId === itemId && item.itemType === itemType);
    } catch (error) {
      console.error('Error checking watch later status:', error);
      return false;
    }
  }

  /**
   * Toggle watch later status with improved 409 handling
   * @param {string} userId - User ID
   * @param {Object} itemData - Item data
   * @returns {Promise} - New watch later status
   */
  async toggleWatchLater(userId, itemData) {
    try {
      console.log('Toggling watch later for:', { userId, itemId: itemData.itemId, itemType: itemData.itemType });
      
      // First, check if item is already in watch later to avoid 409
      const existingItems = await this.getWatchLaterItems(userId, { type: itemData.itemType });
      const isCurrentlyInWatchLater = existingItems.data?.some(item => 
        item.itemId === itemData.itemId && item.itemType === itemData.itemType
      );

      if (isCurrentlyInWatchLater) {
        // Remove from watch later
        await this.removeFromWatchLater(userId, itemData.itemId, itemData.itemType);
        return false;
      } else {
        // Add to watch later
        await this.addToWatchLater(userId, itemData);
        return true;
      }
    } catch (error) {
      console.error('Error toggling watch later:', error);
      throw error;
    }
  }

  /**
   * Handle API errors consistently
   * @param {Error} error - The error object
   * @returns {Error} - Formatted error
   */
  handleError(error) {
    if (error.response) {
      // Server responded with error status
      const message = error.response.data?.message || error.response.data || 'Server error';
      return new Error(`API Error: ${message}`);
    } else if (error.request) {
      // Request was made but no response received
      return new Error('Network error: No response from server');
    } else {
      // Something else happened
      return new Error(`Request error: ${error.message}`);
    }
  }
}

// Export a singleton instance
const watchLaterService = new WatchLaterService();
export default watchLaterService;
